import { useEffect } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import { captureEvent } from "@/lib/analyticsQueue";
import { ANALYTICS_EVENTS } from "@shared/schemas/analytics";
import { WhatsAppIcon } from "./WhatsAppIcon";
import { useCommunityCta } from "./useCommunityCta";

const SOURCE = "floating_button" as const;

/**
 * Floating WhatsApp launcher — replaces the old Todo Bot bubble. Keeps the
 * same "Need help?" pill above the icon, but the icon is the WhatsApp brand
 * mark and tapping it opens the support community group (not an in-app chat).
 * Same fixed corner placement + RTL behaviour as the bot button it replaced.
 */
export const FloatingWhatsAppButton = () => {
  const { t } = useLanguage();
  const { getCommunityHref, openCommunity } = useCommunityCta();

  // Mirror the other community placements: count an impression once on mount.
  useEffect(() => {
    captureEvent(ANALYTICS_EVENTS.COMMUNITY_CTA_VIEW, { source: SOURCE });
  }, []);

  return (
    <a
      href={getCommunityHref()}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => openCommunity(SOURCE)}
      aria-label={t.chatbot.buttonLabel}
      className="group fixed bottom-32 sm:bottom-24 left-6 rtl:left-auto rtl:right-6 z-40 flex flex-col items-center gap-2 animate-in fade-in slide-in-from-bottom-2 duration-500"
    >
      {/* "Need help?" pill — same look as the old bot button. */}
      <span className="rounded-full bg-white dark:bg-card px-3 py-1 text-xs font-semibold text-primary dark:text-card-foreground shadow-md ring-1 ring-black/5 dark:ring-white/10 whitespace-nowrap">
        {t.chatbot.buttonLabel}
      </span>
      <span className="flex h-12 w-12 items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg transition-transform group-hover:scale-110">
        <WhatsAppIcon className="h-6 w-6" />
      </span>
    </a>
  );
};
